import { Box, FormLabel, Skeleton, Stack } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1IconButton } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress'
import { C1Card } from 'components/core/card/C1Card'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { ExpiresAtChip, RelativeTimestamp } from 'components/core/time/display'
import { DecryptCredential } from 'components/pages/admin/settings/vaults/CredentialsList'
import { useAlert } from 'hooks/useAlert'
import { useDrawer } from 'hooks/useDrawer'
import * as jose from 'jose'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { AppUserCredentialValue } from 'lib/actions/settings/vault'
import { KeyRound } from 'lucide-react'
import {
    ListAppUserCredentialsWithVaultRequest,
    SearchAppUserHasAppUserCredentialsRequest,
} from 'pbts/c1/api/app/v1/app_user'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    listAppUserCredentialsWithVaultApi,
    searchAppUserHasAppUserCredentialsApi,
} from 'redux_toolkit/app-user-credentials'

type AppUserCredentialsActionProps = {
    appUser: AppUserValue
}

export const AppUserCredentialsAction: FC<AppUserCredentialsActionProps> = ({ appUser }) => {
    const [showDrawer] = useDrawer()
    const intl = useIntl()

    const onClick = useCallback(
        () => showDrawer(<AppUserCredentialDrawer appUser={appUser} />, { variant: 'sm' }),
        [showDrawer, appUser],
    )

    const { data: appUserHasCredential } = searchAppUserHasAppUserCredentialsApi.useSearchAppUserHasAppUserCredentials(
        SearchAppUserHasAppUserCredentialsRequest.create({
            appId: appUser.appId,
            appUserId: appUser.id,
        }),
    )

    if (!appUserHasCredential?.hasCredentials) {
        return null
    }

    return <C1IconButton
        variant="outlined"
        color="info"
        onClick={onClick}
        aria-label={intl.formatMessage({
            id: 'component_app_user_credentials_action_label',
            defaultMessage: 'View credentials for {app_user}',
        }, { app_user: appUser.displayName })}
    >
        <C1Icon icon={KeyRound} />
    </C1IconButton>
}

interface AppUserCredentialDrawerProps {
    appUser: AppUserValue,
}

const AppUserCredentialDrawer: FC<AppUserCredentialDrawerProps> = ({
    appUser,
}) => {
    const toast = useAlert()
    const {
        data,
        isLoading,
        error,
    } = listAppUserCredentialsWithVaultApi.useListAppUserCredentialsWithVault(
        ListAppUserCredentialsWithVaultRequest.create(
            {
                appId: appUser.appId,
                appUserId: appUser.id,
            },
        ),
    )

    const credentials = data?.flatMap((item) => item) ?? []

    const [keyPair, setKeyPair] = useState<jose.GenerateKeyPairResult>()
    const [readerPublicKeyBytes, setReaderPublicKeyBytes] = useState<Uint8Array>()

    const generateInitialKeyPair = useCallback(async (): Promise<void> => {
        try {
            const generatedKeyPair = await jose.generateKeyPair('ECDH-ES', { extractable: true })
            setKeyPair(generatedKeyPair)
            const readerPublicKey = await jose.exportJWK(generatedKeyPair.publicKey)
            const jwkBytes = new TextEncoder().encode(JSON.stringify(readerPublicKey))
            setReaderPublicKeyBytes(new Uint8Array(jwkBytes))
        } catch (err) {
            console.error('Failed to generate initial key pair:', err)
            toast(
                <FormattedMessage
                    id="decrypt_credential_error"
                    defaultMessage="Error loading password {code}: {msg}"
                    values={{
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }
    }, [toast])

    useEffect(() => {
        if (appUser.appId) {
            generateInitialKeyPair()
        }
    }, [appUser.appId, generateInitialKeyPair])

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <FormattedMessage
                id="component_app_user_credentials_drawer_title"
                defaultMessage="Credentials for {app_user}"
                values={{ app_user: appUser.displayName }}
            />
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <Stack>
                {error &&
                    <APIError
                        title={<FormattedMessage id="component_app_user_credentials_drawer_error" defaultMessage="Error loading credentials" />}
                        err={error}
                        sx={{ mb: 2 }}
                    />
                }
                {isLoading && <Skeleton />}

                {credentials.map((credential: AppUserCredentialValue, index) => (
                    <Box key={`${credential.id}-${index}`}>
                        <C1Card>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <FormLabel sx={{ minWidth: '140px' }}>
                                        <FormattedMessage
                                            id="component_app_user_credentials_password"
                                            defaultMessage="Password"
                                        />
                                    </FormLabel>
                                    {credential.expiresAt && Timestamp.toDate(credential.expiresAt) < new Date() ? (
                                        <C1EmptyIndicator />
                                    ) : (
                                        keyPair && readerPublicKeyBytes ? (
                                            <DecryptCredential
                                                vaultId={credential.vaultId ?? ''}
                                                appId={appUser.appId}
                                                appUserId={appUser.id}
                                                credentialId={credential.id}
                                                keyPair={keyPair}
                                                readerPublicKeyBytes={readerPublicKeyBytes}
                                            />
                                        ) : (<C1CircularProgress />)
                                    )}
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <FormLabel sx={{ minWidth: '140px' }}>
                                        <FormattedMessage id="component_app_user_credentials_created_on" defaultMessage="Created on" />
                                    </FormLabel>
                                    {credential.createdAt ? (
                                        <RelativeTimestamp
                                            date={credential.createdAt}
                                        />
                                    ) : <C1EmptyIndicator />}
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <FormLabel sx={{ minWidth: '140px' }}>
                                        <FormattedMessage id="component_app_user_credentials_expires_on" defaultMessage="Expires on" />
                                    </FormLabel>
                                    {credential.expiresAt ? (
                                        <ExpiresAtChip
                                            timestamp={credential.expiresAt}
                                        />
                                    ) : <C1EmptyIndicator />}
                                </Stack>
                            </Stack>
                        </C1Card>
                    </Box>
                ))}
            </Stack>
        </C1DrawerInner.Content>
    </C1DrawerInner>
}
